import React, { useState, useLayoutEffect, useRef } from 'react'
import styled from 'styled-components'
import SVG from 'react-inlinesvg'

import TriggerIcon from '../../assets/svg/drag-reveal-trigger.svg'

const gradientMask = `linear-gradient(to bottom, rgba(4, 22, 43, 0), rgba(4, 22, 43, 0.03) 5%, rgba(4, 22, 43, 0.09) 9%, rgba(4, 22, 43, 0.16) 14%, rgba(4, 22, 43, 0.24) 18%, rgba(4, 22, 43, 0.34) 22%, rgba(4, 22, 43, 0.44) 26%, rgba(4, 22, 43, 0.54) 31%, rgba(4, 22, 43, 0.64) 37%, rgba(4, 22, 43, 0.73) 44%, rgba(4, 22, 43, 0.82) 52%, rgba(4, 22, 43, 0.89) 61%, rgba(4, 22, 43, 0.95) 72%, rgba(4, 22, 43, 0.99) 85%, #04162b);`

const StyledWrapper = styled.div`
  display: none;

  ${({ theme }) => theme.media.desktop} {
    display: block;
    height: 768px;
    left: 45%;
    max-width: 608px;
    position: absolute;
    top: 0;
    width: 80%;

    :before {
      background-image: linear-gradient(
        to bottom,
        rgba(187, 191, 198, 0.2),
        rgba(238, 238, 238, 0)
      );
      content: '';
      height: 100%;
      left: 100%;
      opacity: 0.4;
      position: absolute;
      top: 0;
      width: 50vw;
    }

    :after {
      background-image: ${gradientMask};
      bottom: 0;
      content: '';
      height: 396px;
      left: 0;
      pointer-events: none;
      position: absolute;
      width: 100%;
      z-index: 1;
    }

    img {
      left: 0;
      position: absolute;
      top: 272px;
      width: 608px;
    }
  }
`

const StyledLeftContainer = styled.div.attrs(({ leftWidth }) => ({
  style: { width: leftWidth },
}))`
  background: #04162b;
  bottom: 0;
  height: 100%;
  left: 0;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  transition: width 0s ease;
  user-select: none;
  width: 100%;
`

const StyledRightContainer = styled.div`
  background: #04162b;
  bottom: 0;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  user-select: none;
  width: 100%;

  :before {
    background-image: linear-gradient(
      to bottom,
      rgba(187, 191, 198, 0.2),
      rgba(238, 238, 238, 0)
    );
    content: '';
    height: 100%;
    left: 0;
    opacity: 0.4;
    position: absolute;
    top: 0;
    width: 100%;
  }
  img {
    border-radius: 8px;
  }
`

const StyledAdditionalContainer = styled.div`
  bottom: 75px;
  height: 377px;
  opacity: ${({ visible }) => (visible ? '1' : '0')};
  overflow: hidden;
  position: absolute;
  right: -80px;
  transform: scale(${({ visible }) => (visible ? '1' : '0.7')});
  transition: all 0.4s ease;
  width: 281px;
  z-index: 2;

  img {
    position: static;
    width: 100%;
  }

  ${({ theme }) => theme.media.bigDesktop} {
    right: -160px;
  }
`

const StyledDragTrigger = styled.div.attrs(({ dragTransform }) => ({
  style: { transform: 'translate3d(' + dragTransform + ', 0, 0)' },
}))`
  bottom: 16px;
  height: 520px;
  left: 0;
  margin-left: -200px;
  position: absolute;
  transition: transform 0s ease;
  width: 400px;
  z-index: 3;

  :before {
    background: #c02428;
    border-radius: 2px;
    bottom: 0;
    content: '';
    left: 50%;
    margin-left: -2px;
    position: absolute;
    top: 0;
    width: 4px;
  }

  :after {
    background-image: ${gradientMask};
    bottom: 0;
    content: '';
    height: 296px;
    left: 50%;
    margin-left: -2px;
    pointer-events: none;
    position: absolute;
    width: 4px;
    z-index: 1;
  }
`

const StyledTriggerIcon = styled(SVG)`
  bottom: 250px;
  cursor: pointer;
  height: 66px;
  left: 50%;
  margin-left: -33px;
  position: absolute;
  width: 66px;
  z-index: 3;

  :before,
  :after {
    animation: pulse 3s infinite;
    border-radius: 50%;
    border: 1px solid #c02428;
    content: '';
    height: 32px;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate3d(-50%, -50%, 0);
    width: 32px;
    z-index: 1;
  }
  
  :after {
    animation-delay: 0.5s;
  }

  :hover {
    :before,
    :after {
      animation: none;
    }
  }

  @keyframes pulse {
    0% {
      height: 32px;
      opacity: 1;
      width: 32px;
    }

    80% {
      height: 86px;
      opacity: 0;
      width: 86px;
    }

    100% {
      height: 32px;
      opacity: 0;
      width: 32px;
    }
  }
`

const DragReveal = () => {
  const wrapper = useRef(null)
  const [drag, setDrag] = useState('76%')
  const [left, setLeft] = useState('50%')
  const [dragging, setDragging] = useState(false)
  const [min] = useState(0)
  const [max, setMax] = useState(608)

  useLayoutEffect(() => {
    setMax(wrapper.current.offsetWidth)
    setLeft(wrapper.current.offsetWidth * 0.5 + 'px')
    setDrag(wrapper.current.offsetWidth * 0.5 + 'px')
  }, [])

  const handleOnMove = e => {
    if (dragging) {
      let x = e.clientX - wrapper.current.offsetLeft

      if (x < min) {
        x = min
      } else if (x > max) {
        x = max
      }
      setDrag(x + 'px')
      setLeft(x + 'px')
    }
  }

  return (
    <StyledWrapper
      ref={wrapper}
      onMouseMove={e => handleOnMove(e)}
      onTouchMove={e => handleOnMove(e)}
      onMouseUp={() => setDragging(false)}
      onTouchEnd={() => setDragging(false)}
    >
      <StyledRightContainer>
        <img
          alt="dahsboard"
          loading="lazy"
          src={require('../../images/dragReveal/dashboard_updated.png')}
        />
      </StyledRightContainer>
      <StyledLeftContainer leftWidth={left}>
        <img
          alt="console"
          loading="lazy"
          src={require('../../images/dragReveal/console@2x.png')}
        />
      </StyledLeftContainer>
      <StyledDragTrigger
        dragTransform={drag}
        onMouseDown={() => setDragging(true)}
        onTouchStart={() => setDragging(true)}
        onMouseLeave={() => setDragging(false)}
      >
        <StyledTriggerIcon src={TriggerIcon} />
      </StyledDragTrigger>
      <StyledAdditionalContainer
        visible={parseInt(drag.slice(0, -2)) < max * 0.6}
      >
        <img
          alt="additional-menu"
          loading="lazy"
          src={require('../../images/dragReveal/additional-menu.png')}
        />
      </StyledAdditionalContainer>
    </StyledWrapper>
  )
}

export default DragReveal
